import React, { useState, useEffect, useRef } from 'react';
import { Form, Col, Button, Alert, Table, Pagination, Spinner } from 'react-bootstrap';
import { NavLink, Link, useLocation } from 'react-router-dom';
import api from '../utils/api';
import donateIcon from '../images/donations.png';
import memoIcon from '../images/edit-24.png';
import useAuth from '../context/useAuth';

const MemberSearch = () => {
    const location = useLocation();
    const { resultsPerPage } = useAuth();
    const limit = parseInt(resultsPerPage, 10);
    const { searchCriteria: initialSearchCriteria = { name: "", spouse: "", address: "", phone: "", memberId: "" }, currentPage: initialPage = 1 } = location.state || {};

    // States
    const [searchCriteria, setSearchCriteria] = useState(initialSearchCriteria);
    const [members, setMembers] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [totalPages, setTotalPages] = useState(1);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [loading, setLoading] = useState(false);
    const pagesToShow = 10;
    const hasMounted = useRef(false);

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            // Perform search when the user comes back with previous search criteria and currentPage
            if (Object.values(searchCriteria).some(value => value) && currentPage > 0 && location.state) {
                performSearch(searchCriteria, currentPage, limit);
            }
        }
    }, [searchCriteria, currentPage, limit, location.state]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setSearchCriteria(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSearch = (event) => {
        event.preventDefault();
        if (!Object.values(searchCriteria).some(value => value)) {
            setError('Please enter at least one search criteria.');
            return;
        }

        // Trim the search criteria here, before submitting
        const trimmedSearchCriteria = Object.fromEntries(
            Object.entries(searchCriteria).map(([key, value]) => [key, value.trim()])
        );

        setCurrentPage(1);  // Reset to page 1 for a new search
        performSearch(trimmedSearchCriteria, 1, limit);
    };

    const performSearch = (searchCriteria, page, limit) => {
        setLoading(true);  // Start loading
        setError(null);    // Reset error
        api.post('/api/filteredMembers', { ...searchCriteria, page, limit })
            .then(res => {
                setLoading(false);  // Stop loading
                setMembers(res.data.data);
                setTotalPages(res.data.totalPages);
                setCurrentPage(page);
                setSearchPerformed(true);
                window.scrollTo(0, 0);  // Scroll to top
            })
            .catch(error => {
                setLoading(false);  // Stop loading
                setError('Failed to fetch members.');
                setMembers([]);
                setSearchPerformed(true);
            });
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        performSearch(searchCriteria, pageNumber, limit);  // Run search with new page number
    };

    // Pagination Logic
    const pageNumbers = [];
    const startPage = Math.floor((currentPage - 1) / pagesToShow) * pagesToShow + 1;
    const endPage = Math.min(startPage + pagesToShow - 1, totalPages);

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
    }

    // Reusable function for links with searchCriteria and currentPage
    const generateLink = (pathname, memberId) => ({
        pathname: `${pathname}/${memberId}`,
        state: { searchCriteria, currentPage }
    });

    return (
        <div className="container p-3 mb-5 bg-white rounded">
            {error && <Alert variant="danger">{error}</Alert>}
            <h4>Search for Members</h4>
            <Form onSubmit={handleSearch}>
                <Form.Row>
                    <Form.Group as={Col} controlId="formName">
                        <Form.Label className="small-font">Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="First or Middle or Last Name"
                            name="name"
                            value={searchCriteria.name}
                            onChange={handleInputChange}
                            className="small-font"
                        />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formSpouse">
                        <Form.Label className="small-font">Spouse Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Spouse First or Middle or Last Name"
                            name="spouse"
                            value={searchCriteria.spouse}
                            onChange={handleInputChange}
                            className="small-font"
                        />
                    </Form.Group>
                </Form.Row>

                <Form.Row>
                    <Form.Group as={Col} controlId="formAddress">
                        <Form.Label className="small-font">Address</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Address, City, State, Zip, Country"
                            name="address"
                            value={searchCriteria.address}
                            onChange={handleInputChange}
                            className="small-font"
                        />
                    </Form.Group>
                </Form.Row>

                <Form.Row>
                    <Form.Group as={Col} controlId="formPhone">
                        <Form.Label className="small-font">Phone</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Phone"
                            name="phone"
                            value={searchCriteria.phone}
                            onChange={handleInputChange}
                            className="small-font"
                        />
                    </Form.Group>
                    <Form.Group as={Col} controlId="formMemberType">
                        <Form.Label className="small-font">Member ID</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Member Id"
                            name="memberId"
                            value={searchCriteria.memberId}
                            onChange={handleInputChange}
                            className="small-font"
                        />
                    </Form.Group>
                </Form.Row>
                <Button
                    variant="primary"
                    className="small-font"
                    type="submit"
                    disabled={!Object.values(searchCriteria).some(value => value)}
                >
                    Search Member
                </Button>
            </Form>

            {loading && (
                <div className="text-center">
                    <Spinner animation="border" />
                </div>
            )}

            {searchPerformed && members.length === 0 && !loading && (
                <Alert variant="warning">No members found.</Alert>
            )}

            {members.length > 0 && !loading && (
                <div>
                    <Table striped bordered hover size="sm" className="small-font">
                        <thead>
                            <tr>
                                <th>Member ID</th>
                                <th>Member Name</th>
                                <th>Spouse Name</th>
                                <th>City</th>
                                <th>Address</th>
                                <th>Primary Phone</th>
                                <th>Secondary Phone</th>
                                <th>Email Exists?</th>
                                <th>Total Donation</th>
                                <th>Add Memo</th>
                                <th>Add Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(member => (
                                <tr key={member.memberId}>
                                    <td className="align-middle">
                                        <Link to={generateLink('/memberDetails', member.memberId)} className="receipt-link">
                                            {member.memberId}
                                        </Link>
                                    </td>
                                    <td className="align-middle">{member.first1} {member.middle1} {member.last1}</td>
                                    <td className="align-middle">{member.first2} {member.middle2} {member.last2}</td>
                                    <td className="align-middle">{member.city}</td>
                                    <td className="align-middle">{member.address}</td>
                                    <td className="align-middle">{member.phone1}</td>
                                    <td className="align-middle">{member.phone2}</td>
                                    <td className="align-middle">
                                        {member?.email ? ('Y') : ''}
                                    </td>
                                    <td className="align-middle">
                                        {member.totalDonation != null && member.totalDonation > 0 ? (
                                            <Link to={generateLink('/donationHistory', member.memberId)} className="receipt-link">
                                                ${member.totalDonation}
                                            </Link>
                                        ) : (
                                            `$${member.totalDonation != null ? member.totalDonation : '0.00'}`
                                        )}
                                    </td>
                                    <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                                        <NavLink
                                            variant="info"
                                            size="sm"
                                            to={generateLink('/memo-management', member.memberId)}
                                            title="Add Memo"
                                        >
                                            <img src={memoIcon} width="24" height="24" alt="view"></img>
                                        </NavLink>
                                    </td>
                                    <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                                        <NavLink
                                            variant="info"
                                            size="sm"
                                            to={generateLink('/addNewReceipt', member.memberId)}
                                            title="Add Receipt"
                                        >
                                            <img src={donateIcon} width="24" height="24" alt="view"></img>
                                        </NavLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                        {startPage > 1 && <Pagination.Ellipsis onClick={() => handlePageChange(startPage - 1)} />}
                        {pageNumbers.map(pageNumber => (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => handlePageChange(pageNumber)}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        ))}
                        {endPage < totalPages && <Pagination.Ellipsis onClick={() => handlePageChange(endPage + 1)} />}
                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default MemberSearch;